import React from "react";
import { Link } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

// NOTE: This is a general, good-faith SaaS template — not legal advice.
// Replace the [BRACKETED] placeholders and have a lawyer review before launch.
const COMPANY = "BizForge AI";
const CONTACT_EMAIL = "[support@yourbusiness.com]";
const JURISDICTION = "[Your State/Country]";
const LAST_UPDATED = "June 2026";

const SECTIONS = [
  {
    h: "1. Agreement to these Terms",
    p: [
      `These Terms of Service ("Terms") govern your access to and use of ${COMPANY} (the "Service"), operated by Monroe Digital Media LLC ("we," "us," or "our"). By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.`,
    ],
  },
  {
    h: "2. The Service",
    p: [
      `${COMPANY} is an all-in-one business operations platform for freelancers, solopreneurs, and small businesses, including client management, invoicing, expense tracking, tasks, calendar, and AI-assisted tools. We may add, change, or remove features at any time.`,
    ],
  },
  {
    h: "3. Your account",
    p: [
      "You must provide accurate information and are responsible for keeping your login credentials secure. You are responsible for all activity that happens under your account. You must be at least 18 years old (or the age of majority in your jurisdiction) to use the Service. Notify us immediately of any unauthorized use.",
    ],
  },
  {
    h: "4. Plans, billing & payments",
    p: [
      "The Service offers Free, Pro, and Business plans. Paid subscriptions are billed in advance on a recurring basis (monthly) through our payment processor, Stripe. By subscribing, you authorize us to charge your payment method on each renewal until you cancel.",
      "You may cancel at any time; cancellation takes effect at the end of the current billing period. Except where required by law, payments are non-refundable and we do not provide refunds or credits for partial periods. We may change pricing with reasonable advance notice.",
    ],
  },
  {
    h: "5. Acceptable use",
    p: [
      "You agree not to: (a) use the Service for anything unlawful, fraudulent, or harmful; (b) upload malware or attempt to breach security; (c) reverse engineer, resell, or copy the Service; (d) send spam or violate others' rights; or (e) overload or disrupt our systems. We may suspend or terminate accounts that violate these Terms.",
    ],
  },
  {
    h: "6. Your content & data",
    p: [
      "You retain ownership of the data and content you add to the Service (\"Your Content\"). You grant us a limited license to host, process, and display Your Content solely to operate and improve the Service for you. You are responsible for the accuracy and legality of Your Content and for having the rights to use it.",
    ],
  },
  {
    h: "7. AI features",
    p: [
      "The Service includes AI-generated content and insights (for example, summaries, drafts, and recommendations). AI output may be inaccurate, incomplete, or unsuitable for your situation. You are responsible for reviewing AI output before relying on it, and it does not constitute financial, legal, tax, or professional advice.",
    ],
  },
  {
    h: "8. Third-party services",
    p: [
      "The Service relies on third-party providers (including payment processing and AI model providers). Your use of those features may be subject to the providers' own terms, and we are not responsible for third-party services.",
    ],
  },
  {
    h: "9. Intellectual property",
    p: [
      `The Service, including its software, design, and branding, is owned by us and protected by intellectual property laws. These Terms do not grant you any right to our trademarks or content except the limited right to use the Service.`,
    ],
  },
  {
    h: "10. Disclaimers",
    p: [
      'The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, whether express or implied, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, error-free, or secure.',
    ],
  },
  {
    h: "11. Limitation of liability",
    p: [
      "To the maximum extent permitted by law, we will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, data, or goodwill. Our total liability for any claim relating to the Service will not exceed the amount you paid us in the 12 months before the claim.",
    ],
  },
  {
    h: "12. Termination",
    p: [
      "You may stop using the Service and delete your account at any time. We may suspend or terminate your access if you breach these Terms or if required by law. Upon termination, your right to use the Service ends; certain provisions (such as ownership, disclaimers, and liability limits) survive.",
    ],
  },
  {
    h: "13. Changes to these Terms",
    p: [
      "We may update these Terms from time to time. If we make material changes, we will provide reasonable notice (for example, in-app or by email). Your continued use of the Service after changes take effect means you accept the updated Terms.",
    ],
  },
  {
    h: "14. Governing law",
    p: [
      `These Terms are governed by the laws of ${JURISDICTION}, without regard to conflict-of-law rules. Any disputes will be resolved in the courts located in ${JURISDICTION}.`,
    ],
  },
  {
    h: "15. Contact us",
    p: [
      `Questions about these Terms? Contact us at ${CONTACT_EMAIL}.`,
    ],
  },
];

export default function Terms() {
  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2.5" data-testid="terms-home-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary"><Zap className="h-5 w-5 text-primary-foreground" strokeWidth={2.5} /></div>
          <span className="font-display text-lg font-bold tracking-tight">BizForge<span className="text-accent">AI</span></span>
        </Link>
        <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="terms-back-link">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8 pb-20">
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Terms of Service</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

        <div className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-600 dark:text-amber-400" data-testid="terms-disclaimer">
          <strong>Please note:</strong> This is a general template provided as a starting point and is not legal advice.
          Replace the bracketed placeholders with your details and have a qualified attorney review it before relying on it.
        </div>

        <div className="mt-8 space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.h} data-testid={`terms-section`}>
              <h2 className="font-display text-lg font-semibold">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="mt-2 text-sm leading-relaxed text-muted-foreground">{para}</p>
              ))}
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
